// Reusable "Affected assets" panel — links Configuration Items to a Service
// Desk ticket (target_type="ticket") or a Jira task (target_type="task").
// Org-level CMDB, so the same component works on both surfaces.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Cube, Plus, X, MagnifyingGlass } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export default function CiLinkPanel({ targetType, targetId }) {
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const [links, setLinks] = useState([]);
  const [all, setAll] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async () => {
    if (!currentOrg || !targetId) return;
    try {
      const { data } = await axios.get(`${API}/cmdb/links`, { ...h(), params: { target_type: targetType, target_id: targetId } });
      setLinks(data || []); setUnavailable(false);
    } catch (e) { if (e?.response?.status === 402) setUnavailable(true); }
  }, [API, h, currentOrg, targetType, targetId]);
  useEffect(() => { load(); }, [load]);

  const loadAll = async () => { try { const { data } = await axios.get(`${API}/cmdb/cis`, h()); setAll(data || []); } catch (_) {} };
  const link = async (ci_id) => {
    try { await axios.post(`${API}/cmdb/links`, { ci_id, target_type: targetType, target_id: targetId }, h()); setOpen(false); setQ(''); load(); } catch (_) {}
  };
  const unlink = async (linkId) => { await axios.delete(`${API}/cmdb/links/${linkId}`, h()); load(); };

  if (unavailable) return null;
  const linkedIds = new Set(links.map(l => l.ci_id));
  const results = all.filter(c => !linkedIds.has(c.ci_id) && (!q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()))).slice(0, 30);

  return (
    <div data-testid="ci-link-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Cube size={13} /> Affected assets</p>
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) loadAll(); }}>
          <PopoverTrigger asChild>
            <button className="text-[11px] text-cyan-600 font-medium inline-flex items-center gap-0.5 hover:text-cyan-700" data-testid="ci-link-add"><Plus size={12} /> Link</button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-2">
            <div className="relative mb-2">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets…" data-testid="ci-link-search"
                className="w-full h-8 pl-8 pr-2 rounded-md border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300" />
            </div>
            <div className="max-h-56 overflow-auto -mx-1">
              {results.length === 0 && <p className="text-xs text-zinc-400 px-2 py-3 text-center">No matching assets</p>}
              {results.map(c => (
                <button key={c.ci_id} onClick={() => link(c.ci_id)} data-testid={`ci-link-option-${c.ci_id}`}
                  className="w-full text-left px-2 py-1.5 rounded-md hover:bg-zinc-100 text-sm flex items-center gap-2">
                  <Cube size={13} className="text-cyan-600 shrink-0" />
                  <span className="truncate">{c.name}</span>
                  <span className="ml-auto text-[10px] text-zinc-400 capitalize">{c.status}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {links.length === 0 ? (
        <p className="text-xs text-zinc-400">No linked assets.</p>
      ) : (
        <div className="space-y-1.5">
          {links.map(l => (
            <div key={l.link_id} className="flex items-center gap-2 bg-cyan-50/60 ring-1 ring-cyan-100 rounded-md px-2.5 py-1.5" data-testid={`ci-linked-${l.ci_id}`}>
              <Cube size={13} className="text-cyan-600 shrink-0" />
              <span className="text-sm text-zinc-800 truncate flex-1">{l.ci?.name}</span>
              <span className="text-[10px] text-zinc-400 capitalize">{l.ci?.status}</span>
              <button onClick={() => unlink(l.link_id)} className="text-zinc-300 hover:text-red-500" data-testid={`ci-unlink-${l.ci_id}`}><X size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
